// SubjectiveQuiz.js
import React, { useState, useEffect } from "react";
import { Button, Card, Col, Form, Row } from "react-bootstrap";
import { MobileTimePicker } from "@mui/x-date-pickers/MobileTimePicker";
import dayjs from "dayjs";
import SubjectiveQueandAnsModal from "./SubjectiveQueandAnsModal";
import Flatpickr from "react-flatpickr";
import "flatpickr/dist/themes/material_blue.css";
import Tippy from "@tippyjs/react";
import "tippy.js/dist/tippy.css";
import { Info } from "react-feather";

const SubjectiveQuiz = ({
    addQuizToContent,
    goBack,
    passingPercentage,
    setPassingPercentage,
    finalAssessment,
    setFinalAssessment,
    quizTimer,
    setQuizTimer,
    scheduledAttempt,
    setScheduledAttempt,
    startDate,
    setStartDate,
    startTime,
    setStartTime,
    endDate,
    setEndDate,
    endTime,
    setEndTime,
    effectiveCourseId,
    activeSection,
    contentDetails,
    updateQuizContent,
    isEditMode,
    activeQuiz,
    updateQuizQuestion,
    resetActiveQuiz,
}) => {
    const [questions, setQuestions] = useState([]);
    const [openModal, setOpenModal] = useState(false);
    const [editingQuestion, setEditingQuestion] = useState(null);
    const [quizExists, setQuizExists] = useState(false);

    // Load existing questions if editing or if activeQuiz has questions
    useEffect(() => {
        // Check if we have an activeQuiz with questions
        // Since we're in SubjectiveQuiz component (contentType === "quizSubjective"),
        // ALL questions in activeQuiz.questions are subjective by definition
        if (activeQuiz?.questions?.length > 0) {
            console.log("SubjectiveQuiz - Loading questions:", {
                isEditMode,
                totalQuestions: activeQuiz.questions.length,
                questions: activeQuiz.questions,
                activeQuizContentType: activeQuiz.contentType
            });
            // No need to filter - all questions in a subjective quiz are subjective
            setQuestions(activeQuiz.questions);
            setQuizExists(true);
        } else {
            // Clear questions if no activeQuiz or no questions
            setQuestions([]);
            setQuizExists(false);
        }
    }, [activeQuiz, isEditMode]);

    const handleAddQuestion = () => {
        setOpenModal(true);
    };

    const handleQuestionAdded = (newQuestion) => {
        const updatedQuestions = editingQuestion
            ? questions.map(q => q.id === editingQuestion.id ? newQuestion : q)
            : [...questions, newQuestion];

        setQuestions(updatedQuestions);
        setEditingQuestion(null);
        setOpenModal(false);
        setQuizExists(true);
    };

    const handleSave = () => {
        if (questions.length === 0) {
            alert("Please add at least one subjective question");
            return;
        }

        // Properly handle quizTimer: only use value if it's not null, undefined, or empty string
        const timerValue = quizTimer !== null && quizTimer !== undefined && quizTimer !== "" 
            ? Number(quizTimer) 
            : null;

        // Ensure all questions have modelAnswer and totalMarks (even if empty/default)
        const questionsWithRequiredFields = questions.map(q => ({
            ...q,
            modelAnswer: q.modelAnswer || q.model_answer || q.answer || "",
            totalMarks: q.totalMarks || q.marks || 10
        }));

        console.log("SubjectiveQuiz - handleSave - quizTimer state:", quizTimer, "converted to:", timerValue);
        console.log("SubjectiveQuiz - handleSave - Questions before processing:", questions);
        console.log("SubjectiveQuiz - handleSave - Questions after ensuring required fields:", questionsWithRequiredFields);

        const quizData = {
            contentTitle: contentDetails.contentTitle,
            contentType: "quizSubjective",  // This is critical!
            category: "quiz",
            sectionNumber: activeSection.sectionNumber,
            sectionId: activeSection.sectionId,
            questions: questionsWithRequiredFields, // Use processed questions
            passingPercentage: Number(passingPercentage) || 70,
            finalAssessment: Boolean(finalAssessment),
            quizTimer: timerValue,
        };

        console.log("Saving Subjective Quiz →", quizData);
        console.log("Saving Subjective Quiz - Full JSON:", JSON.stringify(quizData, null, 2));

        // Now pass the data!
        addQuizToContent(quizData);
    };

    const handleUpdate = () => {
        if (!activeQuiz?.contentNumber) {
            console.error("handleUpdate: activeQuiz.contentNumber is missing", activeQuiz);
            alert("Error: Cannot update quiz. Content number is missing.");
            return;
        }
        
        // Properly handle quizTimer: convert to number if it has a value, otherwise keep as null
        const timerValue = quizTimer !== null && quizTimer !== undefined && quizTimer !== "" 
            ? Number(quizTimer) 
            : null;
        
        // Ensure all questions have modelAnswer and totalMarks (even if empty/default)
        const questionsWithRequiredFields = questions.map(q => ({
            ...q,
            modelAnswer: q.modelAnswer || q.model_answer || q.answer || "",
            totalMarks: q.totalMarks || q.marks || 10
        }));
        
        console.log("SubjectiveQuiz - handleUpdate - quizTimer state:", quizTimer, "converted to:", timerValue);
        console.log("SubjectiveQuiz - handleUpdate - Questions before processing:", questions);
        console.log("SubjectiveQuiz - handleUpdate - Questions after ensuring required fields:", questionsWithRequiredFields);
        
        const updatedQuiz = {
            ...activeQuiz,
            questions: questionsWithRequiredFields, // Use processed questions
            passingPercentage: Number(passingPercentage),
            finalAssessment,
            quizTimer: timerValue,
            contentType: "quizSubjective",
            contentNumber: activeQuiz.contentNumber, // Explicitly preserve contentNumber
            sectionNumber: activeQuiz.sectionNumber, // Explicitly preserve sectionNumber
            sectionId: activeQuiz.sectionId || activeSection?.sectionId, // Preserve sectionId
        };
        
        console.log("SubjectiveQuiz - handleUpdate - updatedQuiz:", updatedQuiz);
        console.log("SubjectiveQuiz - handleUpdate - Full JSON:", JSON.stringify(updatedQuiz, null, 2));
        updateQuizContent(updatedQuiz);
    };

    return (
        <Card>
            <Card.Body>
                <div className="d-flex justify-content-between align-items-center mb-4 border-bottom py-2">
                    <h4 className="mb-0">Subjective Quiz Questions</h4>
                </div>

                <Row className="g-3 mb-4">
                    <Col xs={12} md={6} lg={4}>
                        <div className="mb-3">
                            <label className="form-label fw-medium">Passing Percentage</label>
                            <Form.Control
                                type="number"
                                value={passingPercentage || ""}
                                onChange={(e) => setPassingPercentage(e.target.value)}
                                onWheel={(e) => e.target.blur()}
                                min="0"
                                max="100"
                            />
                        </div>
                    </Col>

                    {/* <Col xs={12} md={6} lg={4}>
                        <div className="mb-3">
                            <label className="form-label fw-medium">Final Assessment</label>
                            <div className="mt-2">
                                <Form.Check
                                    type="switch"
                                    checked={finalAssessment}
                                    onChange={(e) => setFinalAssessment(e.target.checked)}
                                    label="Mark as final assessment"
                                />
                            </div>
                        </div>
                    </Col> */}

                    <Col xs={12} md={6} lg={4}>
                        <div className="mb-3">
                            <label className="form-label fw-medium">Quiz Timer (Minutes)</label>
                            <Form.Control
                                type="number"
                                value={quizTimer || ""}
                                onChange={(e) => {
                                    const value = e.target.value;
                                    console.log("SubjectiveQuiz - Timer changed:", value);
                                    setQuizTimer(value);
                                }}
                                onWheel={(e) => e.target.blur()}
                            />
                        </div>
                    </Col>

                    {/* Scheduled Quiz Toggle */}
                    <Col xs={12} md={6} lg={4}>
                        <div className="mb-3">
                            <label className="form-label fw-medium d-flex align-items-center">
                                Scheduled Quiz
                                <Tippy content="If enabled, students can only take the quiz within the specified window.">
                                    <span className="ms-2">
                                        <Info size={16} className="text-muted" />
                                    </span>
                                </Tippy>
                            </label>
                            <div className="mt-2">
                                <Form.Check
                                    type="switch"
                                    id="scheduled-quiz-switch-subjective"
                                    checked={scheduledAttempt}
                                    onChange={(e) => setScheduledAttempt(e.target.checked)}
                                    label={scheduledAttempt ? "Scheduled" : "Always Available"}
                                />
                            </div>
                        </div>
                    </Col>

                    {scheduledAttempt && (
                        <>
                            {/* Start Date & Time */}
                            <Col xs={12} md={6}>
                                <div className="mb-3">
                                    <label className="form-label fw-medium text-primary">Access Opens (Start)</label>
                                    <Row className="g-2">
                                        <Col>
                                            <Flatpickr
                                                value={startDate}
                                                onChange={([date]) => {
                                                    const formatted = date ? dayjs(date).format("YYYY-MM-DD") : "";
                                                    setStartDate(formatted);
                                                }}
                                                className="form-control text-dark"
                                                placeholder="Select Start Date"
                                                options={{ dateFormat: "Y-m-d" }}
                                            />
                                        </Col>
                                        <Col>
                                            <MobileTimePicker
                                              value={startTime ? dayjs(startTime, "HH:mm") : null}
                                              onChange={(newValue) => {
                                                setStartTime(newValue ? newValue.format("HH:mm") : "");
                                              }}
                                              views={["hours", "minutes"]}
                                              openTo="hours"
                                              slotProps={{
                                                textField: {
                                                  size: "small",
                                                  fullWidth: true,
                                                  className: "bg-white",
                                                  sx: {
                                                    "& .MuiInputBase-root": {
                                                      height: "38px",
                                                    },
                                                  },
                                                },
                                              }}
                                            />
                                        </Col>
                                    </Row>
                                </div>
                            </Col>

                            {/* End Date & Time */}
                            <Col xs={12} md={6}>
                                <div className="mb-3">
                                    <label className="form-label fw-medium text-danger">Access Closes (End)</label>
                                    <Row className="g-2">
                                        <Col>
                                            <Flatpickr
                                                value={endDate}
                                                onChange={([date]) => {
                                                    const formatted = date ? dayjs(date).format("YYYY-MM-DD") : "";
                                                    setEndDate(formatted);
                                                }}
                                                className="form-control text-dark"
                                                placeholder="Select End Date"
                                                options={{ dateFormat: "Y-m-d" }}
                                            />
                                        </Col>
                                        <Col>
                                            <MobileTimePicker
                                              value={endTime ? dayjs(endTime, "HH:mm") : null}
                                              onChange={(newValue) => {
                                                setEndTime(newValue ? newValue.format("HH:mm") : "");
                                              }}
                                              views={["hours", "minutes"]}
                                              openTo="hours"
                                              slotProps={{
                                                textField: {
                                                  size: "small",
                                                  fullWidth: true,
                                                  className: "bg-white",
                                                  sx: {
                                                    "& .MuiInputBase-root": {
                                                      height: "38px",
                                                    },
                                                  },
                                                },
                                              }}
                                            />
                                        </Col>
                                    </Row>
                                </div>
                            </Col>
                        </>
                    )}
                </Row>

                {/* Show questions */}
                {questions.length > 0 ? (
                    questions.map((q, i) => {
                        // Log each question's structure for debugging
                        console.log(`SubjectiveQuiz - Rendering question ${i + 1}:`, {
                            id: q.id,
                            question: q.question,
                            modelAnswer: q.modelAnswer,
                            totalMarks: q.totalMarks,
                            allKeys: Object.keys(q),
                            fullObject: q
                        });
                        
                        return (
                            <Card key={q.id || q._id || `question-${i}`} className="mb-3 border">
                                <Card.Body>
                                    <div className="d-flex justify-content-between align-items-start">
                                        <div className="flex-grow-1">
                                            <strong>Q{i + 1} ({q.totalMarks || q.marks || 10} marks)</strong>
                                            <p className="mb-1 mt-2">{q.question || "No question text"}</p>
                                            {q.modelAnswer && (
                                                <small className="text-muted d-block mt-2">
                                                    <strong>Model Answer:</strong> {q.modelAnswer.substring(0, 100)}
                                                    {q.modelAnswer.length > 100 ? "..." : ""}
                                                </small>
                                            )}
                                            {q.explanation && (
                                                <small className="text-muted d-block mt-1">
                                                    <strong>Explanation:</strong> {q.explanation}
                                                </small>
                                            )}
                                            {!q.modelAnswer && (
                                                <small className="text-warning d-block mt-2">
                                                    ⚠️ No model answer set
                                                </small>
                                            )}
                                            {q.imageQuestion?.length > 0 && (
                                                <small className="text-success d-block mt-1">
                                                    Has {q.imageQuestion.length} image(s)
                                                </small>
                                            )}
                                        </div>
                                        <Button
                                            size="sm"
                                            variant="outline-primary"
                                            className="ms-3"
                                            onClick={() => {
                                                console.log("SubjectiveQuiz - Editing question:", q);
                                                console.log("SubjectiveQuiz - Question properties:", {
                                                    id: q.id,
                                                    question: q.question,
                                                    modelAnswer: q.modelAnswer,
                                                    model_answer: q.model_answer,
                                                    answer: q.answer,
                                                    totalMarks: q.totalMarks,
                                                    marks: q.marks,
                                                    allKeys: Object.keys(q),
                                                    fullObject: JSON.stringify(q, null, 2)
                                                });
                                                setEditingQuestion(q);
                                                setOpenModal(true);
                                            }}
                                        >
                                            Edit
                                        </Button>
                                    </div>
                                </Card.Body>
                            </Card>
                        );
                    })
                ) : (
                    <p className="text-muted">No subjective questions added yet.</p>
                )}

                <div className="d-flex justify-content-between mt-5">
                    {quizExists && (
                        isEditMode ? (
                            <Button variant="primary" onClick={handleUpdate}>
                                Update
                            </Button>
                        ) : (
                            <Button variant="primary" onClick={handleSave}>
                                Done
                            </Button>
                        )
                    )}

                    <Button variant="success" onClick={handleAddQuestion}>
                        + Add Subjective Question
                    </Button>
                </div>
            </Card.Body>

            <SubjectiveQueandAnsModal
                show={openModal}
                handleClose={() => {
                    setOpenModal(false);
                    setEditingQuestion(null);
                }}
                effectiveCourseId={effectiveCourseId}
                activeSection={activeSection}
                onAdded={handleQuestionAdded}
                editMode={!!editingQuestion}
                existingQuestion={editingQuestion}
            />
        </Card>
    );
};

export default SubjectiveQuiz;